import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plus, PencilSimple, Trash, FloppyDisk, X, Package } from "@phosphor-icons/react";
import { useSector } from "@/lib/sectorContext";

// v0.99.50 · Fev/2026 — Categorias por família de setor. Oficina vê
// pneu/peça/óleo; qualquer outro vertical recebe sugestões genéricas
// para não poluir a UI com termos automotivos.
const CATEGORIES_AUTOMOTIVE = [
  { value: "pneu", label: "Pneu" },
  { value: "peca", label: "Peça" },
  { value: "acessorio", label: "Acessório" },
  { value: "oleo_filtro", label: "Óleo / Filtro" },
  { value: "estetica", label: "Produto estético" },
  { value: "comercial", label: "Produto comercial" },
  { value: "outros", label: "Outros" },
];

const CATEGORIES_GENERIC = [
  { value: "principal", label: "Principal" },
  { value: "acessorio", label: "Acessório / Adicional" },
  { value: "consumo", label: "Consumo / Insumo" },
  { value: "kit", label: "Kit / Pacote" },
  { value: "outros", label: "Outros" },
];

const emptyProduct = {
  name: "",
  price_cents: 0,
  category: "outros",
  sku: "",
  description: "",
  in_stock: true,
  active: true,
};

function brl(cents) {
  return ((cents || 0) / 100).toLocaleString("pt-BR", {
    style: "currency", currency: "BRL", minimumFractionDigits: 2,
  });
}

export default function ProductsTab({ onChange, onToast }) {
  const { isAutomotive } = useSector();
  const CATEGORY_SUGGESTIONS = isAutomotive ? CATEGORIES_AUTOMOTIVE : CATEGORIES_GENERIC;
  const emptyCategory = isAutomotive ? "outros" : "principal";
  const emptyHint = isAutomotive
    ? "Cadastre pneus, peças, acessórios — a IA passa a citar preço e disponibilidade no WhatsApp."
    : "Cadastre os produtos que você oferece — a IA passa a citar preço e disponibilidade no WhatsApp.";
  const namePlaceholder = isAutomotive
    ? "Nome do produto (ex.: Pneu Pirelli 195/65 R15)"
    : "Nome do produto (ex.: Produto principal)";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/products");
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      onToast?.("Erro ao carregar produtos.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.name?.trim()) {
      onToast?.("Nome é obrigatório.", "error");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: editing.name.trim(),
        price_cents: Number(editing.price_cents) || 0,
        category: (editing.category || "outros").trim(),
        sku: (editing.sku || "").trim() || null,
        description: editing.description || "",
        in_stock: editing.in_stock !== false,
        active: editing.active !== false,
      };
      if (editing.id) {
        await api.put(`/products/${editing.id}`, payload);
        onToast?.("Produto atualizado.");
      } else {
        await api.post("/products", payload);
        onToast?.("Produto criado.");
      }
      setEditing(null);
      await load();
      onChange?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Erro ao salvar.", "error");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover este produto?")) return;
    try {
      await api.delete(`/products/${id}`);
      onToast?.("Produto removido.");
      await load();
      onChange?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Erro ao remover.", "error");
    }
  };

  if (loading) return <div className="text-sm text-slate-400">Carregando…</div>;

  return (
    <div className="space-y-6" data-testid="products-tab">
      {/* Banner explicativo — IA cita mas não fecha venda. */}
      <div className="flex items-start gap-3 px-4 py-3 rounded-2xl bg-slate-50/70 border border-slate-100">
        <Package size={14} className="text-slate-400 shrink-0 mt-[2px]" />
        <p className="text-[11.5px] text-slate-500 leading-[1.55]">
          A IA pode <span className="text-slate-700 font-medium">citar disponibilidade e preço</span> dos seus produtos no WhatsApp,
          mas <span className="text-slate-700 font-medium">não fecha a venda</span> sozinha — sempre encaminha ao operador humano antes
          de confirmar pagamento ou entrega.
        </p>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-[12px] text-slate-500">
          {items.length} produto{items.length === 1 ? "" : "s"} no catálogo.
        </p>
        <button
          type="button"
          data-testid="products-add"
          onClick={() => setEditing({ ...emptyProduct })}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-[13px]"
        >
          <Plus size={12} weight="bold" /> Novo produto
        </button>
      </div>

      {items.length === 0 ? (
        <div className="px-6 py-10 rounded-2xl border border-dashed border-slate-200 text-center">
          <p className="text-sm text-slate-500">Nenhum produto cadastrado.</p>
          <p className="text-[12px] text-slate-400 mt-1">
            {emptyHint}
          </p>
        </div>
      ) : (
        <div className="border border-slate-100 rounded-2xl overflow-hidden divide-y divide-slate-100">
          {items.map((p) => (
            <div
              key={p.id}
              data-testid={`product-row-${p.id}`}
              className="px-5 py-4 flex items-center gap-4 hover:bg-slate-50"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-slate-900 truncate">{p.name}</span>
                  {p.sku && (
                    <span className="text-[10px] uppercase tracking-wider text-slate-400 border border-slate-200 rounded-full px-1.5 py-0.5 font-mono">
                      {p.sku}
                    </span>
                  )}
                  {p.in_stock === false && (
                    <span className="text-[10px] uppercase tracking-wider text-amber-700 border border-amber-200 bg-amber-50 rounded-full px-1.5 py-0.5">
                      indisponível
                    </span>
                  )}
                  {p.active === false && (
                    <span className="text-[10px] uppercase tracking-wider text-slate-400 border border-slate-200 rounded-full px-1.5 py-0.5">
                      inativo
                    </span>
                  )}
                </div>
                <div className="text-[12px] text-slate-500 mt-0.5 truncate">
                  <span className="capitalize">{p.category || "outros"}</span>
                  {p.description && ` · ${p.description}`}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-semibold text-slate-900 tabular-nums">
                  {brl(p.price_cents)}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => setEditing(p)}
                  className="p-2 rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-900"
                  data-testid={`product-edit-${p.id}`}
                  aria-label="editar"
                >
                  <PencilSimple size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => remove(p.id)}
                  className="p-2 rounded-full hover:bg-slate-100 text-slate-400 hover:text-rose-600"
                  data-testid={`product-remove-${p.id}`}
                  aria-label="remover"
                >
                  <Trash size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-6" data-testid="product-modal">
          <div className="w-full max-w-lg bg-white rounded-3xl shadow-xl">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-900">
                {editing.id ? "Editar produto" : "Novo produto"}
              </h3>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="p-1 rounded-full hover:bg-slate-100 text-slate-500"
              >
                <X size={14} weight="bold" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <input
                type="text"
                placeholder={namePlaceholder}
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                className="w-full px-3 py-2 rounded-full border border-slate-200 text-[13px] focus:border-slate-900 focus:outline-none"
                data-testid="product-modal-name"
              />
              <div className="grid grid-cols-2 gap-3">
                <select
                  value={editing.category}
                  onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                  className="px-3 py-2 rounded-full border border-slate-200 text-[13px] bg-white focus:border-slate-900 focus:outline-none"
                  data-testid="product-modal-category"
                >
                  {CATEGORY_SUGGESTIONS.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Código / SKU (opcional)"
                  value={editing.sku || ""}
                  onChange={(e) => setEditing({ ...editing, sku: e.target.value })}
                  className="px-3 py-2 rounded-full border border-slate-200 text-[13px] focus:border-slate-900 focus:outline-none font-mono"
                  data-testid="product-modal-sku"
                />
              </div>
              <textarea
                placeholder="Descrição curta (opcional)"
                value={editing.description || ""}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 rounded-2xl border border-slate-200 text-[13px] focus:border-slate-900 focus:outline-none resize-none"
              />
              <div>
                <label className="text-[11px] text-slate-500 uppercase tracking-wider">Preço</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0,00"
                  value={editing.price_cents ? (editing.price_cents / 100) : ""}
                  onChange={(e) => setEditing({
                    ...editing,
                    price_cents: Math.round((parseFloat(e.target.value) || 0) * 100),
                  })}
                  className="w-full mt-1 px-3 py-2 rounded-full border border-slate-200 text-[13px] focus:border-slate-900 focus:outline-none tabular-nums"
                  data-testid="product-modal-price"
                />
              </div>
              <div className="space-y-2 pt-1">
                <label className="flex items-center gap-2 text-[13px] text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editing.in_stock !== false}
                    onChange={(e) => setEditing({ ...editing, in_stock: e.target.checked })}
                    data-testid="product-modal-in-stock"
                  />
                  Em estoque (disponível para venda)
                </label>
                <label className="flex items-center gap-2 text-[13px] text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editing.active !== false}
                    onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                    data-testid="product-modal-active"
                  />
                  Ativo (a IA pode citar)
                </label>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="px-4 py-2 rounded-full text-slate-600 hover:bg-slate-100 text-[13px]"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                data-testid="product-modal-save"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-[13px] disabled:opacity-50"
              >
                <FloppyDisk size={12} weight="bold" />
                {saving ? "Salvando…" : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
